import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { rawMaterialFormSchema, type RawMaterialFormData } from '@/schemas/rawMaterialSchemas';
import type { RawMaterial } from '@/types/legacy';
import { canViewMoney } from '@/lib/roleUtils';
import { Loader2, ScanBarcode, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { BarcodeScannerDialog } from '@/components/Barcode';
import { BarcodeInput } from '@/components/Barcode';
import type { BarcodeScanResult } from '@/hooks/useBarcodeScanner';
import { useRawMaterialCategories, useCreateCategory } from '@/hooks/useRawMaterialMovements';

interface RawMaterialLotDraft {
  id?: string;
  batch_number: string;
  roll_number: string;
  quantity: number;
  square_meters: number;
  production_date?: string;
  expiry_date?: string;
  notes?: string;
  is_active: boolean;
  is_in_use: boolean;
}

interface RawMaterialFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rawMaterial?: RawMaterial | null;
  onSuccess?: () => void;
}

export function RawMaterialFormDialog({ open, onOpenChange, rawMaterial, onSuccess }: RawMaterialFormDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { userRole } = useAuth();
  const canSeeMoney = canViewMoney(userRole);
  const [loading, setLoading] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customCategoryName, setCustomCategoryName] = useState('');
  const [lots, setLots] = useState<RawMaterialLotDraft[]>([]);
  const [initialLotIds, setInitialLotIds] = useState<string[]>([]);

  const addLot = () => {
    setLots((prev) => [...prev, {
      batch_number: '',
      roll_number: '',
      quantity: 0,
      square_meters: 0,
      production_date: '',
      expiry_date: '',
      notes: '',
      is_active: true,
      is_in_use: false,
    }]);
  };

  const updateLot = <K extends keyof RawMaterialLotDraft>(index: number, key: K, value: RawMaterialLotDraft[K]) => {
    setLots((prev) => prev.map((lot, i) => (i === index ? { ...lot, [key]: value } : lot)));
  };

  const removeLot = (index: number) => {
    setLots((prev) => prev.filter((_, i) => i !== index));
  };

  const { data: customCategories } = useRawMaterialCategories();
  const createCategory = useCreateCategory();

  // Unit options for dropdown
  const unitOptions = [
    { value: 'm2', label: 'Metros cuadrados (m²)' },
    { value: 'L', label: 'Litros (L)' },
    { value: 'garrafa', label: 'Garrafas' },
    { value: 'galon', label: 'Galones' },
    { value: 'caja', label: 'Cajas' },
    { value: 'paquete', label: 'Paquetes' },
    { value: 'kg', label: 'Kilos (kg)' },
    { value: 'otro', label: 'Otro...' },
  ] as const;

  // Determine initial unit value - check if existing unit is in our list
  const getInitialUnit = (unit?: string) => {
    if (!unit) return 'kg';
    const found = unitOptions.find(opt => opt.value === unit);
    return found ? unit : 'otro';
  };

  const form = useForm<RawMaterialFormData>({
    resolver: zodResolver(rawMaterialFormSchema),
    defaultValues: {
      name: rawMaterial?.name || '',
      code: rawMaterial?.code || '',
      category: rawMaterial?.category || 'other',
      supplier: rawMaterial?.supplier || '',
      batch_number: rawMaterial?.batch_number || '',
      roll_number: rawMaterial?.roll_number || '',
      quantity: rawMaterial?.quantity || 0,
      min_stock: rawMaterial?.min_stock ?? 0,
      roll_count: rawMaterial?.roll_count ?? undefined,
      unit: getInitialUnit(rawMaterial?.unit) as any,
      custom_unit: unitOptions.find(opt => opt.value === rawMaterial?.unit) ? '' : (rawMaterial?.unit || ''),
      production_date: rawMaterial?.production_date || '',
      purchase_date: rawMaterial?.purchase_date || '',
      expiry_date: rawMaterial?.expiry_date || '',
      cost_per_unit: rawMaterial?.cost_per_unit,
      location: rawMaterial?.location || '',
      notes: rawMaterial?.notes || '',
      is_freezer_material: rawMaterial?.is_freezer_material ?? false,
      is_active: rawMaterial?.is_active ?? true,
    },
  });
  const isFreezerMaterial = form.watch('is_freezer_material');
  const selectedUnit = form.watch('unit');

  // Reset form and missing fields when dialog opens/closes
  useEffect(() => {
    if (open) {
      form.reset({
        name: rawMaterial?.name || '',
        code: rawMaterial?.code || '',
        category: rawMaterial?.category || 'other',
        supplier: rawMaterial?.supplier || '',
        batch_number: rawMaterial?.batch_number || '',
        roll_number: rawMaterial?.roll_number || '',
        quantity: rawMaterial?.quantity || 0,
        min_stock: rawMaterial?.min_stock ?? 0,
        roll_count: rawMaterial?.roll_count ?? undefined,
        unit: getInitialUnit(rawMaterial?.unit) as any,
        custom_unit: unitOptions.find(opt => opt.value === rawMaterial?.unit) ? '' : (rawMaterial?.unit || ''),
        production_date: rawMaterial?.production_date || '',
        purchase_date: rawMaterial?.purchase_date || '',
        expiry_date: rawMaterial?.expiry_date || '',
        cost_per_unit: rawMaterial?.cost_per_unit,
        location: rawMaterial?.location || '',
        notes: rawMaterial?.notes || '',
        is_freezer_material: rawMaterial?.is_freezer_material ?? false,
        is_active: rawMaterial?.is_active ?? true,
      });

      // Load existing lots for editing
      if (rawMaterial?.id) {
        api.from('raw_material_lots')
          .select('*')
          .eq('raw_material_id', rawMaterial.id)
          .order('created_at')
          .then(({ data: dbLots, error }) => {
            if (error) {
              console.error('Error loading lots:', error);
              return;
            }
            setInitialLotIds(dbLots?.map((lot: any) => lot.id) || []);
            setLots(dbLots?.map((lot: any) => ({
              id: lot.id,
              batch_number: lot.batch_number || '',
              roll_number: lot.roll_number || '',
              quantity: lot.quantity || 0,
              square_meters: lot.square_meters || 0,
              production_date: lot.production_date || '',
              expiry_date: lot.expiry_date || '',
              notes: lot.notes || '',
              is_active: lot.is_active ?? true,
              is_in_use: lot.is_in_use ?? false,
            })) || []);
          });
      } else {
        setLots([]);
        setInitialLotIds([]);
      }
    } else {
      setMissingFields([]);
      setLots([]);
      setInitialLotIds([]);
    }
  }, [open, rawMaterial, form]);

  // Handle barcode scan result
  const handleBarcodeScan = (data: {
    code: string;
    quantity: number;
    rawMaterial?: RawMaterial;
    source: 'material_inventory' | 'raw_materials' | null;
  }) => {
    // Set the code
    form.setValue('code', data.code);

    // If raw material found, fill the form
    if (data.rawMaterial && data.source === 'raw_materials') {
      form.setValue('name', data.rawMaterial.name);
      form.setValue('category', data.rawMaterial.category);
      form.setValue('supplier', data.rawMaterial.supplier || '');
      form.setValue('batch_number', data.rawMaterial.batch_number || '');
      form.setValue('roll_number', data.rawMaterial.roll_number || '');
      form.setValue('unit', data.rawMaterial.unit);
      form.setValue('roll_count', data.rawMaterial.roll_count ?? undefined);
      form.setValue('production_date', data.rawMaterial.production_date || '');
      form.setValue('cost_per_unit', data.rawMaterial.cost_per_unit);
      form.setValue('notes', data.rawMaterial.notes || '');
      form.setValue('is_freezer_material', data.rawMaterial.is_freezer_material ?? false);

      // If scanned quantity is provided, use it, otherwise use existing
      if (data.quantity > 0) {
        form.setValue('quantity', data.quantity);
      } else {
        form.setValue('quantity', data.rawMaterial.quantity);
      }

      toast({
        title: 'Datos cargados',
        description: `Materia prima "${data.rawMaterial.name}" cargada desde el sistema`,
      });
    } else if (data.quantity > 0) {
      // Only quantity was scanned
      form.setValue('quantity', data.quantity);
    }

    // Check for missing required fields
    const values = form.getValues();
    const missing: string[] = [];
    if (!values.name) missing.push('Nombre');
    if (!values.category) missing.push('Categoría');
    if (!values.unit) missing.push('Unidad');

    // Recommended fields
    const recommended: string[] = [];
    if (!values.supplier) recommended.push('Proveedor');
    if (values.is_freezer_material) {
      if (!values.batch_number) recommended.push('Número de lote');
      if (!values.roll_number) recommended.push('Número de rollo');
      if (!values.roll_count) recommended.push('Cantidad de bobinas/rollos');
      if (!values.production_date) recommended.push('Fecha de producción');
      if (!values.expiry_date) recommended.push('Fecha de caducidad');
    } else if (!values.expiry_date) {
      recommended.push('Fecha de vencimiento');
    }

    setMissingFields([...missing, ...recommended.map(f => `${f} (recomendado)`)]);
  };

  // Handle USB scanner input (hidden input)
  const handleUsbScan = (result: BarcodeScanResult) => {
    form.setValue('code', result.code);
    if (result.quantity > 0) {
      form.setValue('quantity', result.quantity);
    }
    toast({
      title: 'Código escaneado',
      description: `Código: ${result.code}${result.quantity > 0 ? `, Cantidad: ${result.quantity}` : ''}`,
    });
  };

  const onSubmit = async (data: RawMaterialFormData) => {
    setLoading(true);
    try {
      const freezerPayload = data.is_freezer_material
        ? {
            roll_number: data.roll_number || null,
            roll_count: data.roll_count ?? null,
            production_date: data.production_date || null,
          }
        : {
            roll_number: null,
            roll_count: null,
            production_date: null,
          };
      // If unit is 'otro', use the custom_unit value
      const finalUnit = data.unit === 'otro' ? (data.custom_unit || 'otro') : data.unit;

      const payload = {
        name: data.name,
        code: data.code || null,
        category: data.category,
        supplier: data.supplier || null,
        batch_number: data.batch_number || null,
        quantity: data.quantity,
        min_stock: data.min_stock ?? 0,
        square_meters: 0, // Default value - not used in UI
        unit: finalUnit,
        location: data.location || null,
        purchase_date: data.purchase_date || null,
        expiry_date: data.expiry_date || null,
        cost_per_unit: data.cost_per_unit || null,
        notes: data.notes || null,
        is_freezer_material: data.is_freezer_material,
        ...freezerPayload,
        is_active: data.is_active,
      };

      let materialId: string;

      if (rawMaterial) {
        const { error } = await api
          .from('raw_materials')
          .update(payload)
          .eq('id', rawMaterial.id);

        if (error) throw error;
        materialId = rawMaterial.id;
      } else {
        const { data: inserted, error } = await api
          .from('raw_materials')
          .insert([payload])
          .select('id')
          .single();

        if (error) throw error;
        materialId = inserted.id;
      }

      // Save lots
      if (lots.length > 0) {
        const normalizedLots = lots.map((lot) => ({
          raw_material_id: materialId,
          batch_number: lot.batch_number || null,
          roll_number: lot.roll_number || null,
          quantity: lot.quantity || 0,
          square_meters: lot.square_meters || 0,
          production_date: lot.production_date || null,
          expiry_date: lot.expiry_date || null,
          notes: lot.notes || null,
          is_active: lot.is_active,
          is_in_use: lot.is_in_use,
        }));

        // Delete removed lots
        const currentLotIds = lots.filter(l => l.id).map(l => l.id);
        const deletedLotIds = initialLotIds.filter(id => !currentLotIds.includes(id));
        if (deletedLotIds.length > 0) {
          await api.from('raw_material_lots').delete().in('id', deletedLotIds);
        }

        // Update existing lots
        for (const lot of lots.filter(l => l.id)) {
          await api.from('raw_material_lots')
            .update({
              batch_number: lot.batch_number || null,
              roll_number: lot.roll_number || null,
              quantity: lot.quantity || 0,
              square_meters: lot.square_meters || 0,
              production_date: lot.production_date || null,
              expiry_date: lot.expiry_date || null,
              notes: lot.notes || null,
              is_active: lot.is_active,
              is_in_use: lot.is_in_use,
              updated_at: new Date().toISOString(),
            })
            .eq('id', lot.id);
        }

        // Insert new lots
        const newLots = normalizedLots.filter((_, i) => !lots[i].id);
        if (newLots.length > 0) {
          await api.from('raw_material_lots').insert(newLots);
        }
      } else if (initialLotIds.length > 0) {
        // All lots were removed
        await api.from('raw_material_lots').delete().in('id', initialLotIds);
      }

      toast({
        title: rawMaterial ? 'Materia prima actualizada' : 'Materia prima creada',
        description: `La materia prima se ${rawMaterial ? 'actualizó' : 'creó'} correctamente${lots.length > 0 ? ` con ${lots.length} lote(s)` : ''}`,
      });

      queryClient.invalidateQueries({ queryKey: ['raw_materials'] });
      onOpenChange(false);
      form.reset();
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar la materia prima',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{rawMaterial ? 'Editar Materia Prima' : 'Nueva Materia Prima'}</DialogTitle>
          <DialogDescription>
            {rawMaterial ? 'Modifica los datos de la materia prima' : 'Ingresa los datos de la nueva materia prima'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            onKeyDown={(e) => {
              // El Enter final del lector de códigos no debe disparar el submit
              // nativo (guardaría a medias y cerraría el diálogo). Guardar solo
              // con el botón; Enter en textarea sigue funcionando.
              if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
                e.preventDefault();
              }
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Nombre de la materia prima" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Código</FormLabel>
                    <div className="flex gap-2">
                      <FormControl>
                        <Input {...field} placeholder="Ej: RAW-001" />
                      </FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setScannerOpen(true)}
                        title="Escanear código de barras"
                      >
                        <ScanBarcode className="h-4 w-4" />
                      </Button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Hidden barcode input for USB scanner */}
            <BarcodeInput
              onScan={handleUsbScan}
              hidden
              autoFocus={false}
            />

            {/* Missing fields alert */}
            {missingFields.length > 0 && (
              <Alert variant="default" className="border-amber-500/50 bg-amber-500/10">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                <AlertDescription className="text-amber-700 dark:text-amber-400">
                  <strong>Campos pendientes:</strong>
                  <ul className="list-disc list-inside mt-1">
                    {missingFields.map((field, i) => (
                      <li key={i}>{field}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoría *</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        if (value === '__new__') {
                          setShowCustomCategory(true);
                        } else {
                          setShowCustomCategory(false);
                          field.onChange(value);
                        }
                      }}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar categoría" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="resin">Resina</SelectItem>
                        <SelectItem value="fiber">Fibra</SelectItem>
                        <SelectItem value="fabric">Tela</SelectItem>
                        <SelectItem value="core">Núcleo</SelectItem>
                        <SelectItem value="consumable">Consumible</SelectItem>
                        <SelectItem value="catalyst">Catalizador</SelectItem>
                        {/* Custom categories from database */}
                        {customCategories?.filter(c => !c.is_system && c.name).map((cat) => (
                          <SelectItem key={cat.id} value={cat.name}>
                            {cat.description || cat.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="other">Otro</SelectItem>
                        <SelectItem value="__new__" className="text-primary font-medium">
                          <span className="flex items-center gap-1">
                            <Plus className="w-3 h-3" />
                            Nueva categoría...
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Custom category input */}
              {showCustomCategory && (
                <FormItem>
                  <FormLabel>Nueva Categoría *</FormLabel>
                  <div className="flex gap-2">
                    <Input
                      value={customCategoryName}
                      onChange={(e) => setCustomCategoryName(e.target.value)}
                      placeholder="Nombre de la categoría"
                    />
                    <Button
                      type="button"
                      size="sm"
                      disabled={!customCategoryName.trim() || createCategory.isPending}
                      onClick={async () => {
                        if (customCategoryName.trim()) {
                          await createCategory.mutateAsync(customCategoryName.trim());
                          form.setValue('category', customCategoryName.trim());
                          setShowCustomCategory(false);
                          setCustomCategoryName('');
                        }
                      }}
                    >
                      {createCategory.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowCustomCategory(false);
                        setCustomCategoryName('');
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                </FormItem>
              )}

              <FormField
                control={form.control}
                name="supplier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Proveedor</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Nombre del proveedor" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ubicación</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Ej: Estante A-1, Almacén 2, Congelador 3" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_freezer_material"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Material de congeladores</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Activa campos adicionales para bobinas o rollos
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cantidad *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : 0)}
                        step="0.001"
                        min={0.001}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="min_stock"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Stock mínimo (alerta)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value ?? 0}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : 0)}
                        step="0.001"
                        min={0}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="unit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unidad *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar unidad" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {unitOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {selectedUnit === 'otro' && (
                <FormField
                  control={form.control}
                  name="custom_unit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unidad Personalizada *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Ej: bolsas, tubos, etc." />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {!isFreezerMaterial && (
                <FormField
                  control={form.control}
                  name="batch_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Número de Lote</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Número de lote" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            {isFreezerMaterial && (
              <div className="rounded-lg border p-4 space-y-4">
                <div className="font-medium text-sm">Material de congeladores</div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="roll_count"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cantidad de bobinas/rollos</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            {...field}
                            onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                            min={0}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="batch_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Número de Lote</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Número de lote" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="roll_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Número de Rollo</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Número de rollo" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="production_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Fecha de Producción</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="expiry_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Fecha de Caducidad</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            {/* Sección de Lotes */}
            <div className="rounded-lg border p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">Lotes y Rollos</div>
                  <div className="text-xs text-muted-foreground">
                    Registra múltiples lotes con sus números de rollo para trazabilidad
                  </div>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addLot}>
                  <Plus className="w-4 h-4 mr-1" /> Agregar lote
                </Button>
              </div>

              <div className="space-y-3">
                {lots.length === 0 && (
                  <div className="text-sm text-muted-foreground border rounded-md p-3 text-center">
                    No hay lotes registrados. Puedes agregar lotes para llevar control de cada rollo o partida.
                  </div>
                )}

                {lots.map((lot, index) => (
                  <div key={lot.id || index} className="border rounded-md p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-xs uppercase tracking-wide text-muted-foreground">
                        Lote {index + 1}
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeLot(index)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <FormLabel>Número de lote</FormLabel>
                        <Input
                          value={lot.batch_number}
                          onChange={(e) => updateLot(index, 'batch_number', e.target.value)}
                          placeholder="Ej: LOT-2026-001"
                        />
                      </div>
                      <div>
                        <FormLabel>Número de rollo</FormLabel>
                        <Input
                          value={lot.roll_number}
                          onChange={(e) => updateLot(index, 'roll_number', e.target.value)}
                          placeholder="Ej: R01"
                        />
                      </div>
                      <div>
                        <FormLabel>Cantidad</FormLabel>
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          value={lot.quantity}
                          onChange={(e) => updateLot(index, 'quantity', Number(e.target.value || 0))}
                        />
                      </div>
                      <div>
                        <FormLabel>m² (si aplica)</FormLabel>
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          value={lot.square_meters}
                          onChange={(e) => updateLot(index, 'square_meters', Number(e.target.value || 0))}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <FormLabel>Fecha producción</FormLabel>
                        <Input
                          type="date"
                          value={lot.production_date || ''}
                          onChange={(e) => updateLot(index, 'production_date', e.target.value)}
                        />
                      </div>
                      <div>
                        <FormLabel>Fecha caducidad</FormLabel>
                        <Input
                          type="date"
                          value={lot.expiry_date || ''}
                          onChange={(e) => updateLot(index, 'expiry_date', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                        <span className="text-sm">En uso</span>
                        <Switch checked={lot.is_in_use} onCheckedChange={(v) => updateLot(index, 'is_in_use', v)} />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                        <span className="text-sm">Activo</span>
                        <Switch checked={lot.is_active} onCheckedChange={(v) => updateLot(index, 'is_active', v)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {lots.length > 0 && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Total lotes:</span>{' '}
                      <span className="font-semibold">{lots.length}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Cantidad total:</span>{' '}
                      <span className="font-semibold">
                        {lots.reduce((sum, lot) => sum + (lot.quantity || 0), 0).toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">m² totales:</span>{' '}
                      <span className="font-semibold">
                        {lots.reduce((sum, lot) => sum + (lot.square_meters || 0), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="purchase_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha de Compra</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {!isFreezerMaterial && (
                <FormField
                  control={form.control}
                  name="expiry_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fecha de Vencimiento</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormDescription>
                        Importante para resinas y catalizadores
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            {canSeeMoney && (
              <FormField
                control={form.control}
                name="cost_per_unit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Costo por Unidad</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                        step="0.01"
                        min={0}
                        placeholder="0.00"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Notas adicionales" rows={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Activo</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      La materia prima está activa en el sistema
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {rawMaterial ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>

      {/* Barcode Scanner Dialog */}
      <BarcodeScannerDialog
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onConfirm={handleBarcodeScan}
        filterSource="raw_materials"
        title="Escanear Materia Prima"
      />
    </Dialog>
  );
}
